/**
 * AddressCorrectionApi - API service for address correction operations
 *
 * SESSION 197: Address Correction Frontend Admin UI
 *
 * Connects to backend endpoints:
 * - GET /api/address-corrections/pending
 * - POST /api/address-corrections/{id}/approve
 * - POST /api/address-corrections/{id}/reject
 *
 * Backend controller: Backend/MentionedMe.API/Controllers/AddressCorrectionsController.cs
 */
import { apiClient } from './ApiClient';
import type {
  GetAddressCorrectionsParams,
  GetAddressCorrectionsResponse,
  ApproveAddressCorrectionCommand,
  RejectAddressCorrectionCommand
} from '@/domain/entities/AddressCorrection';

/**
 * Base path for address correction API endpoints
 */
const BASE_PATH = '/api/address-corrections';

/**
 * AddressCorrectionApi class
 *
 * Provides type-safe methods for interacting with address correction API
 */
export class AddressCorrectionApi {
  /**
   * Get paginated list of address correction requests
   *
   * @param params - Query parameters (page, pageSize, status filter)
   * @returns Paged result of address corrections
   *
   * @example
   * const result = await addressCorrectionApi.getAddressCorrections({
   *   page: 1,
   *   pageSize: 25,
   *   status: 'Pending'
   * });
   */
  async getAddressCorrections(
    params?: GetAddressCorrectionsParams
  ): Promise<GetAddressCorrectionsResponse> {
    // Build query parameters
    const queryParams = new URLSearchParams();

    if (params?.page) {
      queryParams.append('pageNumber', params.page.toString());
    }
    if (params?.pageSize) {
      queryParams.append('pageSize', params.pageSize.toString());
    }
    if (params?.status && params.status !== 'All') {
      queryParams.append('status', params.status);
    }

    const url = `${BASE_PATH}/pending${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    return apiClient.get<GetAddressCorrectionsResponse>(url);
  }

  /**
   * Approve an address correction request
   *
   * @param id - Address correction request ID (Guid)
   * @param command - Approval request with optional admin notes
   * @returns Promise<void> - Returns 204 No Content on success
   *
   * @example
   * await addressCorrectionApi.approveCorrection('123e4567-e89b-12d3-a456-426614174000', {
   *   adminNotes: 'Address verified and updated'
   * });
   */
  async approveCorrection(id: string, command?: ApproveAddressCorrectionCommand): Promise<void> {
    const url = `${BASE_PATH}/${id}/approve`;
    await apiClient.post<void>(url, command || {});
  }

  /**
   * Reject an address correction request
   *
   * @param id - Address correction request ID (Guid)
   * @param command - Rejection request with reason and optional admin notes
   * @returns Promise<void> - Returns 204 No Content on success
   *
   * @example
   * await addressCorrectionApi.rejectCorrection('123e4567-e89b-12d3-a456-426614174000', {
   *   rejectionReason: 'The requested address is invalid',
   *   adminNotes: 'Could not verify this address'
   * });
   */
  async rejectCorrection(id: string, command: RejectAddressCorrectionCommand): Promise<void> {
    const url = `${BASE_PATH}/${id}/reject`;
    await apiClient.post<void>(url, command);
  }
}

/**
 * Default address correction API instance
 */
export const addressCorrectionApi = new AddressCorrectionApi();
